<template lang="pug">
.manage-collection
  header.manage-collection__header
    .manage-collection__heading
      router-link.manage-collection__back(:to="listPath")
        fai(type="fal" icon="angle-left").size-20
      div
        .d-flex.align-items-center.gap-3
          h1.manage-collection__title {{ isNew ? 'Create Collection' : 'Edit Collection' }}
          span.manage-collection__status(v-if="collection?.status" :class="statusClass")
            | {{ statusLabel }}
        p.manage-collection__subtitle
          | Sticker collection with unlock codes and progress tracking
    .manage-collection__actions
      button.btn-outline(v-if="canPublish" type="button" @click="publishCollection") Publish
      button.btn-outline(v-if="canArchive" type="button" @click="archiveCollection") Archive
      button.btn-outline.btn-outline--danger(
        v-if="canDelete"
        type="button"
        @click="deleteCollection"
      )
        fai(type="fal" icon="trash").size-14
        | Delete
      button.btn-outline(type="button" @click="$router.push(listPath)")
        fai(type="fal" icon="times").size-14
        | Cancel
      button.btn-primary(
        type="button"
        :class="{ 'btn-primary--saved': saved }"
        :disabled="saving"
        @click="saveAll"
      )
        template(v-if="saved")
          fai(type="fal" icon="check").size-14
          | Saved!
        template(v-else)
          fai(type="fal" icon="save").size-16
          | {{ saving ? 'Saving…' : 'Save Collection' }}

  nav.manage-collection__tabs
    button.manage-collection__tab(
      v-for="t in tabs"
      :key="t.value"
      type="button"
      :class="{ 'is-active': activeTab === t.value }"
      :disabled="t.value !== 'setup' && !collectionId"
      @click="activeTab = t.value"
    ) {{ t.label }}

  section.manage-collection__body(v-loading="loading")
    .manage-collection__draft-note(
      v-if="collection?.status === 'DRAFT' && (collection?.items?.length ?? 0) > 0"
    )
      span ⚡
      span
        | Draft: hidden on public sticker pages. Publish the collection to activate
        | unlock codes and show it to collectors.
    collection-setup-tab(
      v-show="activeTab === 'setup'"
      ref="setupRef"
      :org-id="orgId"
      :org-name="orgName"
      :collection-id="collectionId"
      :collection="collection"
      :collectors-count="collectorsCount"
      @refresh="loadCollection"
      @created="onCreated"
    )
    collection-updates-tab(
      v-if="collectionId"
      v-show="activeTab === 'updates'"
      ref="updatesRef"
      :collection-id="collectionId"
      :collection="collection"
      :org-name="orgName"
      @refresh="loadCollection"
    )
    collection-analytics-tab(
      v-if="collectionId"
      v-show="activeTab === 'analytics'"
      :collection-id="collectionId"
      :active="activeTab === 'analytics'"
    )
</template>

<script lang="ts">
import {
  computed, defineComponent, onMounted, ref, watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { ElMessage, ElMessageBox } from 'element-plus';
import globalShared from '@/shared';
import commonShared from '@/views/manage-users-and-organizations/shared';
import CollectionSetupTab from './components/CollectionSetupTab.vue';
import CollectionUpdatesTab from './components/CollectionUpdatesTab.vue';
import CollectionAnalyticsTab from './components/CollectionAnalyticsTab.vue';
import { collectionStatusLabel } from './shared';
import {
  findStickerCollection,
  findStickerCollectionAnalytics,
  mutateStickerCollectionStatus,
  removeStickerCollection,
  mutateStickerCollectionItem,
} from '@/graphs/gql';
import type { StickerCollectionCms } from '@/data/stickerCollection.types';
import withGraphErrors from '@/utils/graphqlResponse';
import isUUID from '@/utils/isUUID';

type SetupTabRef = { save: () => Promise<boolean> };
type UpdatesTabRef = { flush: () => Promise<boolean> };

const TABS = [
  { value: 'setup', label: 'Collection Setup' },
  { value: 'updates', label: 'Published Updates' },
  { value: 'analytics', label: 'Analytics & Collectors' },
];

export default defineComponent({
  name: 'ManageStickerCollection',
  components: {
    CollectionSetupTab,
    CollectionUpdatesTab,
    CollectionAnalyticsTab,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const { makeGraphRequest, getAuthToken, removeItemGetConfirm } = globalShared();
    const { getOrganization } = commonShared();

    const orgId = computed(() => String(route.params.orgId || ''));
    const collectionId = computed(() => {
      if (route.name === 'CreateOrgStickerCollection') return '';
      return String(route.params.collectionId || '');
    });
    const isNew = computed(() => route.name === 'CreateOrgStickerCollection');

    const loading = ref(false);
    const saving = ref(false);
    const saved = ref(false);
    const collection = ref<StickerCollectionCms | null>(null);
    const collectorsCount = ref(0);
    const activeTab = ref('setup');
    const setupRef = ref<SetupTabRef | null>(null);
    const updatesRef = ref<UpdatesTabRef | null>(null);

    const fetchedOrgName = ref('');
    const orgName = computed(() => {
      const selected = store.getters['organizations/GET_SELECTED_ORGANIZATION'];
      if (selected?.id === orgId.value && selected?.name) return selected.name;
      return fetchedOrgName.value || collection.value?.ownerName || '';
    });

    // Super admins open any org's collections without selecting that org first.
    async function loadOrgName() {
      const selected = store.getters['organizations/GET_SELECTED_ORGANIZATION'];
      if (!orgId.value || selected?.id === orgId.value) return;
      try {
        fetchedOrgName.value = (await getOrganization(orgId.value))?.name || '';
      } catch (e) {
        console.error(e);
      }
    }

    const listPath = computed(() => `/manage/orgs/${orgId.value}/sticker-collections`);

    const statusLabel = computed(() => collectionStatusLabel(collection.value?.status || 'DRAFT'));
    const statusClass = computed(
      () => `manage-collection__status--${(collection.value?.status || 'DRAFT').toLowerCase()}`,
    );

    const canPublish = computed(
      () => collection.value?.status === 'DRAFT' || collection.value?.status === 'ARCHIVED',
    );
    const canArchive = computed(() => collection.value?.status === 'PUBLISHED');
    // The API refuses to delete published/archived collections: they are archived instead.
    const canDelete = computed(() => !!collectionId.value && collection.value?.status === 'DRAFT');

    async function loadCollectorsCount() {
      if (!collectionId.value) {
        collectorsCount.value = 0;
        return;
      }
      const response = await makeGraphRequest(
        'query',
        findStickerCollectionAnalytics,
        { collectionId: collectionId.value, skip: 0, take: 1 },
        getAuthToken(),
      );
      collectorsCount.value = response?.data?.findStickerCollectionAnalytics?.startedCount ?? 0;
    }

    async function loadCollection() {
      if (!collectionId.value) {
        collection.value = null;
        return;
      }
      loading.value = true;
      try {
        const response = await makeGraphRequest(
          'query',
          findStickerCollection,
          { id: collectionId.value },
          getAuthToken(),
        );
        if (response?.errors?.length) {
          ElMessage.error(response.errors[0]?.message || 'Failed to load collection');
          return;
        }
        collection.value = response?.data?.findStickerCollection ?? null;
      } catch (e) {
        console.error(e);
        ElMessage.error('Failed to load collection');
      } finally {
        loading.value = false;
      }
    }

    async function saveAll() {
      if (!setupRef.value?.save) return;
      saving.value = true;
      try {
        const setupOk = await setupRef.value.save();
        const updatesOk = updatesRef.value ? await updatesRef.value.flush() : true;
        if (setupOk && updatesOk) {
          saved.value = true;
          setTimeout(() => { saved.value = false; }, 2000);
        }
      } finally {
        saving.value = false;
      }
    }

    function onCreated(id: string) {
      router.replace({
        name: 'EditOrgStickerCollection',
        params: { orgId: orgId.value, collectionId: id },
      });
    }

    async function setStatus(status: 'PUBLISHED' | 'ARCHIVED') {
      const response = await withGraphErrors(makeGraphRequest(
        'mutation',
        mutateStickerCollectionStatus,
        { collectionId: collectionId.value, status },
        getAuthToken(),
      ));
      if (response?.errors?.length) {
        ElMessage.error(response.errors[0]?.message || 'Failed to update status');
        return;
      }
      ElMessage.success(status === 'PUBLISHED' ? 'Collection published' : 'Collection archived');
      loadCollection();
    }

    async function publishCollection() {
      if (!collectionId.value) return;
      if ((collection.value?.items?.length ?? 0) === 0) {
        ElMessage.warning('Add at least one sticker before publishing');
        return;
      }
      const itemCount = collection.value?.items?.length ?? 0;
      const requiredCount = collection.value?.requireAllStickersToComplete === false
        ? collection.value?.stickersRequiredToComplete ?? 0
        : 0;
      if (requiredCount > itemCount) {
        ElMessage.warning(
          `The collection is set to complete at ${requiredCount} stickers but has ${itemCount}. `
            + 'Add stickers or lower the number.',
        );
        return;
      }
      // A required sticker with no active code could never be collected once published.
      const locked = collection.value?.items?.find(
        (it) => it.requiresCode && !it.codes?.some((code) => code.isActive),
      );
      if (locked) {
        ElMessage.warning(
          `Add at least one unlock code to "${locked.sticker?.title || 'this sticker'}" `
            + 'before publishing.',
        );
        return;
      }
      try {
        await ElMessageBox.confirm(
          'Publishing activates unlock codes for collectors. Continue?',
          'Publish collection',
          { type: 'warning', customClass: 'c-confirm', confirmButtonText: 'Publish' },
        );
      } catch {
        return;
      }
      setStatus('PUBLISHED');
    }

    async function archiveCollection() {
      if (!collectionId.value) return;
      try {
        await ElMessageBox.confirm(
          'Archived collections cannot redeem codes. Continue?',
          'Archive collection',
          { type: 'warning', customClass: 'c-confirm', confirmButtonText: 'Archive' },
        );
      } catch {
        return;
      }
      setStatus('ARCHIVED');
    }

    function deleteCollection() {
      if (!collectionId.value) return;
      removeItemGetConfirm(async () => {
        const response = await withGraphErrors(makeGraphRequest(
          'mutation',
          removeStickerCollection,
          { collectionId: collectionId.value },
          getAuthToken(),
        ));
        if (response?.errors?.length) {
          ElMessage.error(response.errors[0]?.message || 'Failed to delete');
          return false;
        }
        router.push(listPath.value);
        return true;
      }, {
        title: 'Delete collection',
        message: `"${collection.value?.title || 'This collection'}" will be permanently deleted. `
          + "This can't be undone.",
      });
    }

    // Returning from the full sticker editor with ?stickerId= adds that sticker.
    async function maybeAddStickerFromQuery() {
      const stickerId = route.query.stickerId as string | undefined;
      if (!stickerId || !collectionId.value) return;
      // Clear first so a remount mid-request can't add the sticker twice.
      router.replace({ name: route.name as string, params: route.params, query: {} });
      if (!isUUID(stickerId)) return;
      const response = await withGraphErrors(makeGraphRequest(
        'mutation',
        mutateStickerCollectionItem,
        {
          input: {
            collectionId: collectionId.value,
            stickerId,
            sortOrder: collection.value?.items?.length ?? 0,
            requiresCode: false,
          },
        },
        getAuthToken(),
      ));
      if (response?.errors?.length) {
        ElMessage.error(response.errors[0]?.message || 'Failed to add new sticker');
      } else {
        ElMessage.success('New sticker added to collection');
        await loadCollection();
      }
    }

    onMounted(async () => {
      loadOrgName();
      await loadCollection();
      await maybeAddStickerFromQuery();
      loadCollectorsCount();
    });

    watch(collectionId, () => {
      loadCollection();
      loadCollectorsCount();
    });

    return {
      tabs: TABS,
      orgId,
      orgName,
      collectionId,
      isNew,
      loading,
      saving,
      saved,
      collection,
      collectorsCount,
      activeTab,
      setupRef,
      updatesRef,
      listPath,
      statusLabel,
      statusClass,
      canPublish,
      canArchive,
      canDelete,
      loadCollection,
      saveAll,
      onCreated,
      publishCollection,
      archiveCollection,
      deleteCollection,
    };
  },
});
</script>

<style lang="scss" scoped>
@import './collection-cms';

.manage-collection {
  min-height: 100vh;
  background: $page-bg;
  display: flex;
  flex-direction: column;
}

// px-8 pt-7 pb-0 flex items-start justify-between
.manage-collection__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 28px 32px 0;
  flex-wrap: wrap;
}

.manage-collection__heading {
  display: flex;
  align-items: center;
  gap: 12px;
}

.manage-collection__back {
  display: inline-flex;
  margin-top: 4px;
  color: $gray-400;
  font-size: 20px;
  transition: color 0.15s;

  &:hover {
    color: $gray-700;
  }
}

// text-3xl font-bold text-gray-900 leading-tight
.manage-collection__title {
  font-size: 30px;
  line-height: 1.25;
  font-weight: 700;
  color: $gray-900;
  margin: 0;
}

.manage-collection__subtitle {
  @include text-sm;
  color: $gray-400;
  margin: 2px 0 0;
}

.manage-collection__status {
  @include text-xs;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 9999px;

  &--draft {
    background: $amber-50;
    color: $amber-600;
  }

  &--published {
    background: $teal-50;
    color: $teal;
  }

  &--archived {
    background: $gray-100;
    color: $gray-500;
  }
}

.manage-collection__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  flex-wrap: wrap;
}

.btn-outline {
  @include btn-outline;
  gap: 8px;

  &--danger:hover:not(:disabled) {
    color: $red-400;
    border-color: $red-400;
    background: transparent;
  }
}

.btn-primary {
  @include btn-primary;
  gap: 8px;
  padding: 8px 16px;

  &--saved,
  &--saved:hover:not(:disabled) {
    background: $green-500;
    border-color: $green-500;
  }
}

// px-8 mt-5; border-b border-gray-200
.manage-collection__tabs {
  display: flex;
  margin: 20px 32px 0;
  border-bottom: 1px solid $gray-200;
}

// px-5 py-2.5 text-sm font-medium border-b-2 -mb-px
.manage-collection__tab {
  padding: 10px 20px;
  @include text-sm;
  font-weight: 500;
  font-family: inherit;
  color: $gray-500;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  cursor: pointer;
  transition: color 0.15s;

  &:hover:not(:disabled) {
    color: $gray-700;
  }

  &.is-active {
    color: $teal;
    border-bottom-color: $teal;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

// px-8 py-6
.manage-collection__body {
  flex: 1;
  padding: 24px 32px;
}

.manage-collection__draft-note {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 1152px;
  margin-bottom: 20px;
  background: $amber-50;
  border: 1px solid $amber-100;
  border-radius: 8px;
  padding: 8px 12px;
  @include text-xs;
  color: $amber-700;
}
</style>
